// src/components/SubPayTermsInline.js
// ---------------------------------------------------------------------------
// Sits right under a Tech Assign list. For every assigned tech who is a
// Sub Contractor in Staff it asks: paid Daily or by Project?
//   Daily    how many workers and $ per day per worker (default $350, or the
//            sub's own rate from Staff). Days come from the job's dates.
//   Project  one price for the job (rolls over when the job is Continued).
// Employees never see these options. Only SUB_PAY_ROLES (Admin) see it.
//
// It edits the FORM's job object through onChange({ subPayTerms,
// subcontractorPay }), so the page's normal Save writes it. The owed line is
// worked out here too, so the numbers are right even before the server sync.
//
//   <SubPayTermsInline job={editedJob} users={usersList} userRole={userRole}
//     onChange={(patch) => setEditedJob((prev) => ({ ...prev, ...patch }))} />
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import { isSubcontractor } from "../utils/contractorIdentity";
import {
  applySubPayTerms,
  canSeeSubPay,
  computeTermOwed,
  DEFAULT_DAILY_RATE,
  isContinuationJob,
  isOwnTerm,
  isTermStale,
  newTerm,
  normalizeSubName,
  termKey,
} from "../utils/subPayTerms";
import { loadPeople, makeSameSub } from "../utils/subPayPayments";
import { personAtDate } from "../utils/payHistory";
import SubPayTermFields, { fmtMoney } from "./SubPayTermFields";
import SubPayDirections from "./SubPayDirections";
import "./SubPayPanel.css";

const nameForms = (u) =>
  [`${u.firstName || ""} ${u.lastName || ""}`.trim(), u.companyName, u.name, u.displayName]
    .filter(Boolean)
    .map(normalizeSubName);

/** The users doc for a techAssign name, when that person is a Sub Contractor. */
export const findSubUser = (name, users = []) => {
  const key = normalizeSubName(name);
  if (!key) return null;
  return users.find((u) => isSubcontractor(u) && nameForms(u).includes(key)) || null;
};

export const subDefaultRate = (savedUser, day) => {
  // The sub's default rate in effect on the job's date (rates have start dates).
  const user = savedUser ? personAtDate(savedUser, day) : savedUser;
  const r = Number(user?.subDailyRate);
  return Number.isFinite(r) && r > 0 ? r : DEFAULT_DAILY_RATE;
};

export const findTerm = (terms = {}, name = "") =>
  terms[termKey(name)] ||
  Object.values(terms).find((t) => normalizeSubName(t?.name) === normalizeSubName(name)) ||
  null;

/**
 * Who owns an edited term. Editing a term on a job makes it THIS job's term
 * (its typed days apply here) - except a project price that rolled over from
 * an earlier job, which stays owned by that job so it is never owed twice.
 * Switching a continued job to a NEW project price asks first.
 * Returns the term to save, or null to cancel.
 */
export const guardTermEdit = (job, prev, edited) => {
  if (!job?.id) return edited; // new job: owns its terms
  const prevOwn = prev ? isOwnTerm(job, prev) : true;
  const becameProject = edited.mode === "project" && prev && prev.mode !== "project";
  if (!prevOwn && prev?.mode === "project" && edited.mode === "project") {
    return { ...edited, setOnJobId: prev.setOnJobId, projectJobId: prev.projectJobId };
  }
  if (becameProject && isContinuationJob(job)) {
    const ok = window.confirm(
      "This job was continued from an earlier work order.\n\nSet a NEW project price owed on this job? " +
        "(The earlier job's project price is not changed.)"
    );
    if (!ok) return null;
    return { ...edited, projectAmount: "", setOnJobId: job.id, projectJobId: job.id };
  }
  return { ...edited, setOnJobId: job.id };
};

const SubPayTermsInline = ({ job, users = [], userRole, onChange }) => {
  const [people, setPeople] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const allowed = canSeeSubPay(userRole);

  useEffect(() => {
    if (!allowed) return;
    let live = true;
    loadPeople()
      .then((p) => live && setPeople(p))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [allowed]);

  const sameSub = useMemo(() => makeSameSub(people), [people]);

  const techs = useMemo(() => {
    const list = Array.isArray(job?.techAssign) ? job.techAssign : job?.techAssign ? [job.techAssign] : [];
    const seen = new Set();
    return list
      .map((t) => String(t || "").trim())
      .filter((t) => {
        const k = normalizeSubName(t);
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
  }, [job?.techAssign]);

  const subs = useMemo(
    () => techs.map((name) => ({ name, user: findSubUser(name, users) })).filter((s) => s.user),
    [techs, users]
  );

  const terms = useMemo(() => job?.subPayTerms || {}, [job?.subPayTerms]);

  const push = (nextTerms) => {
    const result = applySubPayTerms({ ...job, subPayTerms: nextTerms }, { sameSub });
    onChange({ subPayTerms: result.subPayTerms, subcontractorPay: result.subcontractorPay });
  };

  // Dates changed in the form -> daily totals follow.
  const lastSig = useRef("");
  useEffect(() => {
    if (!allowed || !job) return;
    const stale = Object.values(terms).some((t) => t?.name && isTermStale(job, t));
    const sig = `${job.startDate}|${job.endDate}`;
    if (stale && sig !== lastSig.current) {
      lastSig.current = sig;
      push(terms);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.startDate, job?.endDate, allowed]);

  if (!allowed || !subs.length) return null;

  const editor = () => {
    const u = getAuth()?.currentUser;
    return u?.displayName || u?.email || "";
  };

  const setTerm = (name, edited) => {
    const prev = findTerm(terms, name);
    const term = guardTermEdit(job, prev, edited);
    if (!term) return;
    const key = termKey(name);
    const next = { ...terms };
    // drop any older entry for the same person under another key
    Object.keys(next).forEach((k) => {
      if (k !== key && normalizeSubName(next[k]?.name) === normalizeSubName(name)) delete next[k];
    });
    next[key] = {
      ...term,
      name: term.name || name,
      updatedAt: new Date().toISOString(),
      updatedBy: editor(),
    };
    push(next);
  };

  const removeTerm = (name) => {
    const t = findTerm(terms, name);
    if (!t) return;
    const next = { ...terms };
    Object.keys(next).forEach((k) => {
      if (normalizeSubName(next[k]?.name) === normalizeSubName(name)) delete next[k];
    });
    // Take back the owed line this term wrote, unless it was changed by hand.
    const lines = (Array.isArray(job.subcontractorPay) ? job.subcontractorPay : []).filter((l) => {
      if (!sameSub(l?.subcontractorName || "", name)) return true;
      const cos = Array.isArray(l.changeOrders) ? l.changeOrders : [];
      return cos.length > 0 || Number(l.basePay) !== Number(t.computedAmount);
    });
    onChange({ subPayTerms: next, subcontractorPay: lines });
  };

  return (
    <div className="spp-inline">
      <div className="spp-inline__head">
        <strong>Sub Contractor pay</strong>
        <span className="spp-badge">Admin only</span>
        <button type="button" className="spp-link" onClick={() => setShowHelp(true)}>
          Directions
        </button>
      </div>
      {subs.map(({ name, user }) => {
        const term = findTerm(terms, name);
        const calc = term ? computeTermOwed(job, term) : null;
        return (
          <div key={name} className="spp-inline__sub">
            <div className="spp-inline__name">
              <span>{name}</span>
              {term ? (
                <>
                  <span className="spp-owed">{fmtMoney(calc.amount)}</span>
                  <button type="button" className="spp-link spp-link--danger" onClick={() => removeTerm(name)}>
                    Remove pay
                  </button>
                </>
              ) : (
                <span className="spp-inline__pick">
                  Paid by:
                  <button type="button" className="spp-chip" onClick={() => setTerm(name, newTerm(name, { mode: "daily", rate: subDefaultRate(user, job?.startDate), jobId: job?.id }))}>
                    Daily
                  </button>
                  <button type="button" className="spp-chip" onClick={() => setTerm(name, newTerm(name, { mode: "project", rate: subDefaultRate(user, job?.startDate), jobId: job?.id }))}>
                    Project
                  </button>
                </span>
              )}
            </div>
            {term && <SubPayTermFields job={job} term={term} onChange={(t) => setTerm(name, t)} />}
          </div>
        );
      })}
      <SubPayDirections open={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
};

export default SubPayTermsInline;
